// src/components/AskVillage.js
import { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  Button,
  VStack,
  Text,
  HStack,
  Avatar,
  Flex,
  Spinner,
  ModalCloseButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaRobot } from "react-icons/fa";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";

// 🔥 Pulse Animation
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 0.9; }
`;

// ✅ Gemini config (Create React App style)
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// Use current models: gemini-2.5-flash or gemini-2.5-flash-lite
const GEMINI_MODEL = "gemini-2.5-flash";
const GEMINI_ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

// 🧠 Village + Temple context (English + Roman Telugu mix, with correct temple name)
const VILLAGE_CONTEXT = `
You are GramadevaAI, a friendly assistant for Thokada Village and
"SRI ANNAPURNA SAMETHA VISWESWARA SWAMY ALAYAM THOKADA" (Thokada Sivalayam) in Andhra Pradesh.

LANGUAGE RULES:
- Reply in a MIX of:
  • Simple, clear ENGLISH
  • TELUGU written in ENGLISH letters (Roman script)
- Do NOT use Telugu script. Example style:
  "Namaste! Mana Thokada Sivalayam gurinchi adiginanduku chala santosham.
   This temple is SRI ANNAPURNA SAMETHA VISWESWARA SWAMY ALAYAM THOKADA.
   Idi mana Thokada gramam lo unna oka prachina alayam."
- Tone must be warm, devotional and friendly, like a local villager or temple volunteer explaining things with love.

TEMPLE NAME:
- Always refer to the temple as:
  "SRI ANNAPURNA SAMETHA VISWESWARA SWAMY ALAYAM THOKADA"
- You can also say:
  "Mana Thokada Sivalayam" or "mana Sivalayam" in a friendly way.

IMPORTANT STATIC SOURCES (TREAT AS REFERENCE CONTEXT ONLY; DON'T CLAIM LIVE BROWSING):
1) Official temple / village site: https://sivalayamthokada.org/
   - About temple: https://sivalayamthokada.org/about
   - Temple deeds / works: https://sivalayamthokada.org/deeds
2) Village data site: https://villageinfo.in/andhra-pradesh/east-godavari/rajanagaram/thokada.html

DONATION DATA:
- You may sometimes receive dynamic DONATION_DATA text in the prompt.
- That data comes from: https://api.sivalayamthokada.org/api/donations
- Each donation item looks like:
  { "id": "...", "donorFirstName": "Mahesh", "donorLastName": "Vasantha", "amount": 1000.0, "timestamp": "2025-08-02T07:21:13.676Z" }
- Use that DONATION_DATA for questions about latest donors / amounts / donation statistics.

INSTRUCTIONS:
- If the question is about Thokada village, SRI ANNAPURNA SAMETHA VISWESWARA SWAMY ALAYAM THOKADA,
  temple history, pooja details, donations, location, facilities, etc.:
  👉 Answer as a local guide in mixed English + Roman Telugu.
  👉 Prefer information that could come from the temple site and village info.
  👉 For donation-related questions, use any DONATION_DATA provided in the prompt.
  👉 If you are not sure about exact timings, amounts, or new projects, say politely:
     "Idi maaravachu, dayachesi official website leda aalaya nirvahakulanu sampradinchi taaja samacharam telsukondi."

- If the user asks general questions (Java, movies, general Hinduism, etc.):
  👉 Still answer in the same mixed style (simple English + Roman Telugu).

- Never confidently invent very specific facts (exact bank account number, UPI ID, etc.).
- When you are unsure, say you are not fully sure instead of making up details.
`.trim();

// 🔍 Simple helper to detect if a question is about donations
function isDonationQuestion(text) {
  const q = text.toLowerCase();
  return (
    q.includes("donation") ||
    q.includes("donate") ||
    q.includes("kanuka") ||
    q.includes("kanukalu") ||
    q.includes("daanam") ||
    q.includes("dana") ||
    q.includes("donalu")
  );
}

// 🧾 Fetch donation data and build a text summary for Gemini
async function buildDonationContext() {
  const res = await fetch("https://api.sivalayamthokada.org/api/donations");
  if (!res.ok) {
    throw new Error("Failed to fetch donation data");
  }
  const data = await res.json();

  if (!Array.isArray(data) || data.length === 0) {
    return "DONATION_DATA: prastutam donation list lo entries levu leka data andubaatulo ledu.";
  }

  // Take latest 20 donations (assuming API returns oldest->newest)
  const sorted = [...data].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );
  const latest = sorted.slice(-20);

  const totalAmount = sorted.reduce((sum, d) => sum + (d.amount || 0), 0);

  const latestLines = latest
    .map((d) => {
      const name = `${d.donorFirstName ?? ""} ${d.donorLastName ?? ""}`.trim();
      const amount = d.amount ?? "";
      const date = d.timestamp
        ? new Date(d.timestamp).toISOString().slice(0, 10)
        : "";
      return `- ${name} : ₹${amount} on ${date}`;
    })
    .join("\n");

  return `
DONATION_DATA:
- Total recorded donation amount (approx): ₹${totalAmount}
- Latest ${latest.length} donations:
${latestLines}

NOTE FOR ASSISTANT:
Use this DONATION_DATA only for giving examples like:
• Iteevalli daatalu evaru?
• Sumaaaru entha motthamlo viraalalu vachchayi?
Do NOT reveal any sensitive information beyond what is listed here.
`.trim();
}

export default function AskVillage({ isOpen, setIsOpen }) {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "👋 Namaste! Nenu GramadevaAI — short answers only. Mee prashna adandi.",
    },
  ]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const showTooltip = useBreakpointValue({ base: false, md: true });

  const handleAsk = async () => {
    if (!question.trim()) return;

    if (!GEMINI_API_KEY) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "⚠️ Gemini API key ledu. REACT_APP_GEMINI_API_KEY .env lo set chesi app restart cheyandi.",
        },
      ]);
      return;
    }

    const newMessages = [...messages, { sender: "user", text: question }];
    setMessages(newMessages);
    setQuestion("");
    setLoading(true);

    let donationContext = "";

    try {
      // If the question is about donations, fetch live donation data
      if (isDonationQuestion(question)) {
        try {
          donationContext = await buildDonationContext();
        } catch (e) {
          console.error("Donation data fetch error:", e);
          donationContext =
            "DONATION_DATA: donation samacharam server nundi techche prayatnam lo lopam vachchindi.";
        }
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000);

      // --- concise RESPONSE_STYLE added here ---
      const promptText = `
${VILLAGE_CONTEXT}

RESPONSE_STYLE:
- Give a very short, crisp summary (max 2–3 short sentences).
- Use the mixed language rule (simple English + Roman Telugu).
- Keep the reply under ~200 characters if possible.
- If listing numbers or donations, keep it to 1–2 short lines or bullets.

Additional dynamic data for this question:
${
  donationContext ||
  "DONATION_DATA: ee prashnaku additional donation data pampabadaledu."
}

User question:
${question}
`.trim();

      const res = await fetch(`${GEMINI_ENDPOINT}?key=${GEMINI_API_KEY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal,
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: promptText,
                },
              ],
            },
          ],
        }),
      });

      clearTimeout(timeout);
      const data = await res.json();
      console.log("GEMINI DEBUG RESPONSE:", data);

      let answer =
        "🙏 GramadevaAI prastutam konchem samasya edurkontondi. Koncham sepu tarvata malli try cheyandi.";

      if (data?.candidates?.[0]?.content?.parts?.length) {
        answer = data.candidates[0].content.parts
          .map((p) => p.text || "")
          .join("")
          .trim();
      } else if (data.error?.message) {
        answer =
          "⚠️ Gemini API error: " +
          data.error.message +
          " — dayachesi API key / model / quota check cheyandi.";
      }

      // --- Client-side truncation/fallback to ensure short replies ---
      const MAX_CHARS = 250; // recommended 200-300
      let shortAnswer = (answer || "").trim();

      if (shortAnswer.length > MAX_CHARS) {
        // try to cut cleanly at end of sentence under limit
        const truncated = shortAnswer.slice(0, MAX_CHARS);
        const lastPeriod = Math.max(
          truncated.lastIndexOf(". "),
          truncated.lastIndexOf("! "),
          truncated.lastIndexOf("? "),
        );
        if (lastPeriod > Math.floor(MAX_CHARS * 0.4)) {
          shortAnswer = truncated.slice(0, lastPeriod + 1).trim();
        } else {
          // fallback: cut and append ellipsis
          shortAnswer = truncated.trim() + "...";
        }
      }

      // Ensure we never set an empty bot message
      if (!shortAnswer) {
        shortAnswer =
          "🙏 Sorry — konchem info lekunda vundi. Dayachesi inka koncham detail ivvandi leda website choodandi.";
      }

      setMessages((prev) => [...prev, { sender: "bot", text: shortAnswer }]);
    } catch (err) {
      console.error("GEMINI FETCH ERROR:", err);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "🙏 GramadevaAI ki network/timeout problem vachchindi. Koncham taruvata try cheyandi.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    navigate("/");
  };

  return (
    <>
      {/* Floating GramadevaAI Button – hidden when modal is open */}
      {!isOpen && (
        <VStack
          position="fixed"
          bottom="90px"
          right="20px"
          zIndex="9999"
          role="group"
          spacing={1}
        >
          <Tooltip
            label="Ask GramadevaAI"
            hasArrow
            placement="left"
            isDisabled={!showTooltip}
          >
            <IconButton
              icon={
                <Box
                  bgGradient="linear(to-r, orange.400, red.500)"
                  borderRadius="full"
                  p={2}
                  boxShadow="0 0 15px rgba(255, 87, 34, 0.7)"
                >
                  <FaRobot size={26} color="white" />
                </Box>
              }
              onClick={() => setIsOpen(true)}
              bg="transparent"
              _hover={{ transform: "scale(1.2)" }}
              aria-label="Ask GramadevaAI"
              animation={`${pulse} 2s infinite`}
              borderRadius="full"
              size="lg"
            />
          </Tooltip>

          {/* Always-visible label — the Tooltip above only appears on
              hover (md+ screens), so touch-only devices previously had
              no way to identify what this button does. This small pill
              makes it clear on every screen size, including About page. */}
          <Text
            fontSize="9px"
            fontWeight="700"
            color="orange.700"
            bg="white"
            px={2}
            py="2px"
            borderRadius="full"
            boxShadow="0 2px 6px rgba(0,0,0,0.15)"
            userSelect="none"
          >
            Ask AI
          </Text>
        </VStack>
      )}

      {/* Chat Modal */}
      <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader bg="orange.500" color="white" fontWeight="bold">
            🤖 Ask GramadevaAI
          </ModalHeader>

          <ModalCloseButton
            color="white"
            _hover={{ bg: "orange.600" }}
            onClick={handleClose}
          />

          <ModalBody>
            <VStack
              spacing={3}
              align="stretch"
              maxH="400px"
              overflowY="auto"
              p={2}
            >
              {messages.map((msg, i) => (
                <Flex
                  key={i}
                  justify={msg.sender === "user" ? "flex-end" : "flex-start"}
                >
                  {msg.sender === "bot" && (
                    <Avatar size="sm" mr={2} name="AI" />
                  )}
                  <Box
                    bg={msg.sender === "user" ? "teal.500" : "gray.200"}
                    color={msg.sender === "user" ? "white" : "black"}
                    px={3}
                    py={2}
                    borderRadius="lg"
                    maxW="70%"
                    boxShadow="sm"
                  >
                    <Text>{msg.text}</Text>
                  </Box>
                  {msg.sender === "user" && (
                    <Avatar size="sm" ml={2} name="You" />
                  )}
                </Flex>
              ))}

              {loading && (
                <Flex align="center">
                  <Avatar size="sm" mr={2} name="AI" />
                  <Box
                    bg="gray.200"
                    px={3}
                    py={2}
                    borderRadius="lg"
                    maxW="70%"
                    boxShadow="sm"
                  >
                    <HStack>
                      <Spinner size="sm" />
                      <Text fontSize="sm">AI alochistondi...</Text>
                    </HStack>
                  </Box>
                </Flex>
              )}
            </VStack>

            <HStack mt={4}>
              <Input
                placeholder="Mee prashnanu ikkada type cheyandi..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                borderRadius="full"
                bg="gray.50"
                _focus={{
                  borderColor: "orange.400",
                  boxShadow: "0 0 0 1px orange",
                }}
              />
              <Button
                onClick={handleAsk}
                colorScheme="orange"
                borderRadius="full"
                px={6}
                isLoading={loading}
              >
                Ask
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
